import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {
  AdminBusinessInvoice,
  AdminPaginatedEnvelope,
  BusinessInvoiceListParams,
  BusinessOverdraftTotal,
  CreateBusinessInvoicePayload,
  UpdateBusinessInvoiceStatusPayload,
} from '@/types';

/**
 * Fee-invoice endpoints.
 */
/**
 * Some endpoints wrap the document in `data`, some return it bare. Typed rather
 * than reached through `any`, so the unsafe-member-access rules hold.
 */
type MaybeWrapped<T> = T | {data: T};

const unwrap = <T>(body: MaybeWrapped<T>): T =>
  body && typeof body === 'object' && 'data' in body ? body.data : body;

export const invoiceService = {
  getInvoices: async (
    filters: BusinessInvoiceListParams,
  ): Promise<AdminPaginatedEnvelope<AdminBusinessInvoice[]>> => {
    const urlParams = toQueryString({page: 0, limit: 10, ...filters});
    const url = `/v2/admin/business/invoices?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AdminBusinessInvoice[]>>(url);
    // The envelope carries page/total alongside `data`, and callers read the
    // whole envelope.
    return {...response.data, data: response.data?.data};
  },

  getInvoice: async (invoiceId: string): Promise<AdminBusinessInvoice> => {
    const response = await axios.get<MaybeWrapped<AdminBusinessInvoice>>(
      `/v2/admin/business/invoices/${invoiceId}`,
    );
    return unwrap(response.data);
  },

  /**
   * What an invoice for this business and period would bill back from the fee
   * overdrafts it drew. Called as the operator picks the business and dates, so
   * the amount the form shows is the total for exactly that window.
   */
  getOverdraftTotal: async (params: {
    businessId: string;
    fromDate?: string;
    toDate?: string;
    currency?: string;
  }): Promise<BusinessOverdraftTotal> => {
    // Only defined filters reach the query string: toQueryString stringifies
    // every entry it is given, so an absent one would be sent as "undefined".
    const urlParams = toQueryString({
      businessId: params.businessId,
      ...(params.fromDate ? {fromDate: params.fromDate} : {}),
      ...(params.toDate ? {toDate: params.toDate} : {}),
      ...(params.currency ? {currency: params.currency} : {}),
    });
    const response = await axios.get<MaybeWrapped<BusinessOverdraftTotal>>(
      `/v2/admin/business/invoices/overdraft-total?${urlParams}`,
    );
    return unwrap(response.data);
  },

  createInvoice: async (
    payload: CreateBusinessInvoicePayload,
  ): Promise<AdminBusinessInvoice> => {
    const response = await axios.post<MaybeWrapped<AdminBusinessInvoice>>(
      '/v2/admin/business/invoices',
      payload,
    );
    return unwrap(response.data);
  },

  updateInvoiceStatus: async (
    invoiceId: string,
    payload: UpdateBusinessInvoiceStatusPayload,
  ): Promise<AdminBusinessInvoice> => {
    const response = await axios.patch<MaybeWrapped<AdminBusinessInvoice>>(
      `/v2/admin/business/invoices/${invoiceId}/status`,
      payload,
    );
    return unwrap(response.data);
  },
};
